/**
 * Run history for one automation routine (CDID / AHC / APD): the heartbeat
 * strip plus the per-session table, every row clickable into the session's
 * flight-recorder detail page.
 */
import { Fragment, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import type { AutobuildKind, AutobuildSessionSummary } from '@a7y/types';
import { api } from '@/lib/api';
import {
  ACTIVITY_COMPONENT_LABELS,
  fmtDuration,
  fmtLocal,
  fmtLocalEnd,
  fmtUtc,
  KIND_LABELS,
  OUTCOME_STYLES,
  cycleDurationSeconds,
  sessionDurationSeconds,
  useNow,
} from './shared';
import { CopyButton } from '@/components/CopyButton';
import { OutcomeBadge } from './OutcomeBadge';
import { RunnerBadge } from './RunnerBadge';
import { IngressBadge } from './IngressBadge';
import { TriggerBadge } from './TriggerBadge';
import { StageChips } from './StageChips';

/**
 * The most recent runs the table will draw.
 *
 * The API bounds its history SERVER-SIDE, partitioned by (kind, stamp prefix,
 * has-tickets) with each root's children riding along (A7Y-3962 D3) — measured
 * live, a CDID request at limit 30 returns 486 rows spanning ~4.8 days, where
 * the retired per-component bound held about 3.5 hours of ingress attempts. This
 * bounds what the page actually draws (TPF, 2026-09-09), and it counts TOP-LEVEL
 * rows: a nested worker is detail inside its cycle's row, not a row of its own.
 */
const MAX_ROWS = 50;

/**
 * One session and the sessions it dispatched.
 *
 * A CDID cycle is two components, not one: the implementation session
 * orchestrates, and N `cdid-worker.yml` runs do the implementation. Both are
 * genuine Activity — the workers are where the tickets, the durations and most
 * of the failures actually are — so neither can be filtered out the way
 * content-free `ingress-scheduler` attempts are. What was wrong was listing
 * them as flat peers: ~10 workers per cycle, interleaved by start time, with
 * nothing tying any of them to the cycle that dispatched them.
 */
interface SessionGroup {
  session: AutobuildSessionSummary;
  children: AutobuildSessionSummary[];
}

/**
 * The components that schedule work rather than do it.
 *
 * None runs on a fleet (0 of 2,135 recorded a runner in the 7 days to
 * 2026-09-15) and none takes longer than a poll (medians 7s, 0s, 55s).
 *
 * But being machinery is NOT sufficient to hide the row — see
 * `isMachineryNoise`. A scheduler attempt that dispatched work is the recorded
 * OWNER of that work's outcome, and hiding it hides the outcome: measured
 * 2026-09-15, CDID landed five tickets and four of the five landings were
 * attributed to an `ingress-…` stamp, so a view without those rows reported
 * `Resolved 0` on every row of a day that shipped five tickets.
 */
const MACHINERY_COMPONENTS: ReadonlySet<AutobuildSessionSummary['component']> = new Set([
  'ingress-scheduler',
  'hot-lane',
  'lander',
]);

/**
 * Nest each session under its dispatcher, preserving the newest-first order the
 * API returned at both levels.
 *
 * A child is nested ONLY when its parent is in the same response. The server
 * bound rides each root's children along with it (A7Y-3962 D3), so this is
 * usually satisfied — but a worker whose root fell outside the bound can still
 * outlive its orchestrator's window, and a session logged before the
 * `parent-session` marker existed carries no parentage at all. Both render at the top level:
 * unattributed activity is still activity, and hiding it would be a worse lie
 * than showing it unnested.
 */
function groupByParent(sessions: readonly AutobuildSessionSummary[]): SessionGroup[] {
  const present = new Set(sessions.map((s) => s.stamp));
  const children = new Map<string, AutobuildSessionSummary[]>();
  const roots: AutobuildSessionSummary[] = [];
  for (const session of sessions) {
    const parent = session.parentStamp;
    if (parent && parent !== session.stamp && present.has(parent)) {
      const bucket = children.get(parent);
      if (bucket) bucket.push(session);
      else children.set(parent, [session]);
    } else {
      roots.push(session);
    }
  }
  return roots.map((session) => ({ session, children: children.get(session.stamp) ?? [] }));
}

/** The heartbeat strip: one clickable tick per session, oldest → newest. */
function HeartbeatStrip({ sessions }: { sessions: AutobuildSessionSummary[] }) {
  const navigate = useNavigate();
  const ordered = [...sessions].reverse();
  return (
    <div className="flex items-end gap-1" aria-label="Run history strip">
      {ordered.map((s) => (
        <button
          key={s.stamp}
          type="button"
          title={`${s.stamp} — ${OUTCOME_STYLES[s.outcome].label}`}
          onClick={() => navigate(`/admin/automation/${s.kind}/${s.stamp}`)}
          className={`h-6 w-2.5 rounded-sm ${OUTCOME_STYLES[s.outcome].dot} hover:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400 transition-opacity`}
        />
      ))}
    </div>
  );
}

/**
 * One table row. `leading` occupies the Session cell's gutter — a disclosure
 * button on a session that dispatched others, a nesting rail on one that was
 * dispatched, nothing on a plain row — so the row markup itself stays single.
 */
function SessionRow({
  s,
  now,
  leading,
  dispatched = [],
}: {
  s: AutobuildSessionSummary;
  now: number;
  leading?: React.ReactNode;
  /** The sessions this row dispatched, so Duration can report the CYCLE. */
  dispatched?: readonly AutobuildSessionSummary[];
}) {
  return (
    <tr className="hover:bg-zinc-50 dark:hover:bg-zinc-900 align-top">
      {/* An ABSOLUTE url, not the router path the row already links to: this
          exists to be pasted somewhere else — a ticket, a Slack thread, an
          agent's prompt — where a bare `/admin/automation/...` addresses
          nothing. `window.location.origin` keeps it honest about which console
          it came from rather than hardcoding a host. */}
      <td className="px-1 py-2">
        <CopyButton
          value={`${window.location.origin}/admin/automation/${s.kind}/${s.stamp}`}
          label={`link to the ${KIND_LABELS[s.kind]} run started ${fmtLocal(s.startedAt)}`}
        />
      </td>
      {/* The key column is WHEN, not which. A session stamp is a machine key —
          `ingress-34939121211-1`, `worker-33600873821-1` — and at those lengths
          it blew through its column and sat on top of Component. It is also not
          the question a reader arrives with: they scan for a run at a time they
          remember. The stamp keeps its job of addressing the row (it is the
          href, and the hover title) and is shown as a field on the session's
          own page. */}
      <td className="px-3 py-2">
        {/* The timestamp owns line one alone. Badges used to sit inline beside
            it under a shared `whitespace-nowrap`, which cannot shrink — so on
            any row carrying one they pushed straight out of the column and
            printed over Component. They wrap onto their own line instead:
            `empty:hidden` keeps the row a single line on the majority that have
            no badge at all. */}
        <div className="flex items-center gap-1.5 whitespace-nowrap">
          {leading}
          <Link
            to={`/admin/automation/${s.kind}/${s.stamp}`}
            title={`${s.stamp} — ${fmtUtc(s.startedAt)}`}
            className="whitespace-nowrap tabular-nums text-xs text-zinc-800 dark:text-zinc-200 hover:underline"
          >
            {fmtLocal(s.startedAt)}
          </Link>
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-1 empty:mt-0 empty:hidden">
          <TriggerBadge manuallyTriggered={s.manuallyTriggered} />
          <IngressBadge ingress={s.ingress} />
        </div>
        {s.sessionLabel && (
          <div className="mt-1 max-w-sm break-words text-xs text-zinc-600 dark:text-zinc-400">
            {s.sessionLabel}
          </div>
        )}
      </td>
      {/* Component names the physical workflow layer behind the row, and only
          CDID has more than one: `cdid.yml`, `cdid-worker.yml`,
          `cdid-hot-lane.yml`, `cdid-lander.yml` and the ingress scheduler map
          to five components in AUTO_SESSION_ACTIVITY_CONTRACTS, while AHC and
          APD each have exactly one workflow that writes an activity row and so
          read "Implementation session" on every row. A column that cannot vary
          is 12rem of width carrying no information. CDID keeps it; the badge in
          the Session cell still carries the ingress distinction either way. */}
      {s.kind === 'cdid' && (
        <td className="px-3 py-2 text-xs text-zinc-700 dark:text-zinc-300">
          {ACTIVITY_COMPONENT_LABELS[s.component]}
        </td>
      )}
      <td className="px-3 py-2">
        <RunnerBadge runnerHost={s.runnerHost} runnerName={s.runnerName} />
      </td>
      {/* The badge alone. A skip reason is a sentence — `production deploy
          withheld — immutable candidate 609409b…` — and stacking it under the
          badge cost this column 4rem of width on every row to show two clamped
          lines of one row's prose. It lives on the session's own page, which
          has the room to show all of it; the row's job is to say WHICH session
          to open. `title` is not a substitute either: it is invisible until
          hovered and unreachable by keyboard. */}
      <td className="px-3 py-2 align-top">
        <OutcomeBadge outcome={s.outcome} />
      </td>
      {/* Stage settles independently of Outcome/Finished/Duration — a
          cycle's session ends in seconds, its tickets land hours later,
          written by OTHER sessions (A7Y-3962 D5). CDID-only: AHC/APD carry
          no `CdidLifecycleState` pipeline. */}
      {s.kind === 'cdid' && (
        <td className="px-3 py-2 align-top">
          <StageChips stages={s.stages} now={now} />
        </td>
      )}
      <td className="px-3 py-2 text-xs whitespace-nowrap">
        {s.endedAt ? (
          <span
            title={`${fmtUtc(s.endedAt)} — the session stamp is UTC; displayed times are yours`}
          >
            {fmtLocalEnd(s.startedAt, s.endedAt)}
          </span>
        ) : (
          <span className="text-zinc-400">—</span>
        )}
      </td>
      {/* No `whitespace-nowrap`: every settled duration (`52m 03s`, `1h 06m`)
          fits the column on one line, and only a RUNNING row carries the
          `(elapsed)` marker. Letting that one wrap is cheaper than sizing every
          row for the rarest, most transient one. */}
      <td
        className="px-3 py-2 text-xs tabular-nums"
        title={
          dispatched.length > 0
            ? `the whole cycle; this session itself ran ${fmtDuration(sessionDurationSeconds(s, now))}`
            : undefined
        }
      >
        {s.outcome === 'skipped' ? (
          <span className="text-zinc-400">—</span>
        ) : (
          <>
            {fmtDuration(cycleDurationSeconds(s, dispatched, now))}
            {s.outcome === 'running' ? ' (elapsed)' : ''}
          </>
        )}
      </td>
      {/* AHC FILES TICKETS; IT DOES NOT IMPLEMENT THEM. Resolved and Deferred
          count the implementation vocabulary (landed/adopted/shipped/
          superseded, and deferred), which an AHC run never emits — so both
          columns rendered a hard `0` on every row, and a measured zero is worse
          than an absent column because it reads as a finding. One column with
          the number AHC actually produces replaces them. */}
      {s.kind === 'ahc' ? (
        <td className="px-3 py-2 text-xs text-right tabular-nums">
          {s.filedTicketCount > 0 ? (
            <span
              className="text-blue-600 dark:text-blue-400"
              title={`${s.filedTicketCount} ticket${s.filedTicketCount === 1 ? '' : 's'} filed from this run's findings`}
            >
              {s.filedTicketCount}
            </span>
          ) : (
            <span className="text-zinc-400">0</span>
          )}
        </td>
      ) : (
        <>
      {/* Resolved: landed / adopted / shipped / superseded. A running
                    session has resolved nothing yet, so show what it claimed —
                    blue, matching the `batched` chip on the detail page. */}
      <td className="px-3 py-2 text-xs text-right tabular-nums">
        {s.outcome === 'running' ? (
          s.ticketCount > 0 ? (
            <span
              className="text-blue-600 dark:text-blue-400"
              title={`${s.ticketCount} claimed, none resolved yet`}
            >
              {s.ticketCount}
            </span>
          ) : (
            <span className="text-zinc-400">—</span>
          )
        ) : s.resolvedTicketCount > 0 ? (
          <span
            className="text-emerald-600 dark:text-emerald-400"
            title={
              s.ticketCount > s.resolvedTicketCount
                ? `${s.resolvedTicketCount} resolved of ${s.ticketCount} touched`
                : undefined
            }
          >
            {s.resolvedTicketCount}
          </span>
        ) : (
          <span className="text-zinc-400">0</span>
        )}
      </td>
      {/* Deferred: ran out of session clock and handed to a later run.
                    Amber, not red — the ticket keeps its state and is picked up
                    next session; it is a throughput signal, not a failure. */}
      <td className="px-3 py-2 text-xs text-right tabular-nums">
        {s.outcome === 'running' ? (
          <span className="text-zinc-400">—</span>
        ) : s.deferredTicketCount > 0 ? (
          <span
            className="text-amber-600 dark:text-amber-400"
            title={`${s.deferredTicketCount} deferred to a later session (session deadline)`}
          >
            {s.deferredTicketCount}
          </span>
        ) : (
          <span className="text-zinc-400">0</span>
        )}
      </td>
        </>
      )}
      <td className="px-3 py-2 text-xs text-right tabular-nums">
        {s.warningCount > 0 ? (
          <span className="text-amber-600 dark:text-amber-400">{s.warningCount}</span>
        ) : (
          <span className="text-zinc-400">0</span>
        )}
      </td>
      <td className="px-3 py-2 text-xs text-right tabular-nums">
        {s.errorCount > 0 ? (
          <span className="text-red-600 dark:text-red-400">{s.errorCount}</span>
        ) : (
          <span className="text-zinc-400">0</span>
        )}
      </td>
    </tr>
  );
}

export function RunHistory({ kind }: { kind: AutobuildKind }) {
  const now = useNow();
  // MEASURED, 7 days to 2026-09-15, 2,363 CDID-family runs: ingress-scheduler
  // 1,224 (52%, median 7s, never a runner), lander 470 (20%), hot-lane 441
  // (19%, median 0s) — against 186 workers (median 24m 33s) and 36
  // implementation sessions. So 91% of this table was scheduling machinery and
  // 9% was engineering, drawn identically, and the page read as a blur.
  //
  // What hides is machinery that did NOTHING — an attempt that armed no
  // session, claimed no ticket and owns no landing. An attempt that did arm
  // something is a cycle and stays, because it is where that cycle's outcome
  // is recorded (`isMachineryNoise`).
  const [showMachineryRuns, setShowMachineryRuns] = useState(false);
  // Which orchestrators are expanded, by stamp. Collapsed by default: the
  // workers are the detail of a cycle, and defaulting them open reproduces
  // exactly the wall of undifferentiated rows this grouping exists to end.
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());
  const history = useQuery({
    queryKey: ['autobuild-history', kind],
    queryFn: () => api.getAutobuildHistory(kind),
    refetchInterval: 60_000,
  });

  if (history.isLoading) {
    return <div className="p-6 text-sm text-zinc-500">Loading run history…</div>;
  }
  if (history.isError) {
    return (
      <div className="p-4 rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/40 text-sm text-red-700 dark:text-red-300">
        Run history failed to load. Check the Worker logs for /admin/autobuild/history.
      </div>
    );
  }
  const allSessions = history.data?.sessions ?? [];
  // Every stamp some other session names as its dispatcher. Computed over the
  // FULL set and before grouping, because whether a row armed anything decides
  // whether it may be hidden.
  const dispatchers = new Set(
    allSessions.map((s) => s.parentStamp).filter((stamp): stamp is string => Boolean(stamp))
  );

  /**
   * Machinery with nothing to show for itself.
   *
   * The content test is the load-bearing half. A scheduler attempt or hot-lane
   * firing that dispatched a session, claimed a ticket, or owns a landing is
   * the CYCLE — the unit this page exists to show — and its 43-second duration
   * is the dispatcher's own, not the work's. Hiding it on component alone
   * removed the rows that carry `Resolved`, which is how a day that landed
   * five tickets came to read `Resolved 0` on every visible row.
   *
   * A lander wave is the exception and hides unconditionally: its landings are
   * attributed to the cycle that produced them (A7Y-3962 D2), so the wave row
   * is the one machinery row that genuinely duplicates another.
   */
  const isMachineryNoise = (s: AutobuildSessionSummary): boolean => {
    if (s.component === 'lander') return true;
    if (!MACHINERY_COMPONENTS.has(s.component)) return false;
    return !dispatchers.has(s.stamp) && s.ticketCount === 0 && s.resolvedTicketCount === 0;
  };

  // Counted over the FULL set, never over what is currently shown: deriving it
  // from the filtered list makes the count zero the moment the box is ticked,
  // which removes the checkbox and traps the reader in the expanded view.
  const machineryCount = allSessions.filter(isMachineryNoise).length;
  const sessions = showMachineryRuns ? allSessions : allSessions.filter((s) => !isMachineryNoise(s));
  const visibleGroups = groupByParent(sessions);
  const groups = visibleGroups.slice(0, MAX_ROWS);
  const truncated = visibleGroups.length - groups.length;
  if (allSessions.length === 0) {
    return (
      <div className="p-6 text-sm text-zinc-500">
        No recorded {KIND_LABELS[kind]} runs yet. The flight recorder started on 2026-07-24 — runs
        appear here as they happen.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {machineryCount > 0 && (
        <label className="flex items-center gap-2 text-xs text-zinc-600 dark:text-zinc-400">
          <input
            type="checkbox"
            checked={showMachineryRuns}
            onChange={(e) => setShowMachineryRuns(e.target.checked)}
            className="rounded border-zinc-300 dark:border-zinc-700"
          />
          Show idle scheduling machinery — attempts that armed nothing, and lander waves (
          {machineryCount}
          {showMachineryRuns ? ' shown' : ' hidden'})
        </label>
      )}
      {/* Roots only. A tick per row would let ~10 workers per cycle drown the
          cycle they belong to, so a green orchestrator with three failed
          workers would read identically to a clean one. Dispatched sessions
          are the detail inside a tick, not ticks of their own. */}
      <HeartbeatStrip sessions={groups.map((g) => g.session)} />

      {/* Said rather than silently dropped: without this a reader cannot tell a
          capped list from a complete one, and 50 is a display bound, not the
          end of the history. */}
      {truncated > 0 && (
        <p className="text-[11px] text-zinc-400">
          Showing the {MAX_ROWS} most recent — {truncated} older{' '}
          {truncated === 1 ? 'run is' : 'runs are'} not drawn.
        </p>
      )}
      <div className="overflow-x-auto rounded-lg border border-zinc-200 dark:border-zinc-800">
        <table
          className={`${kind === 'cdid' ? 'min-w-[78.75rem]' : kind === 'ahc' ? 'min-w-[55.25rem]' : 'min-w-[58.25rem]'} w-full table-fixed text-sm`}
        >
          {/* One <col> per <th>, and the widths are sized to the widest thing
              each column actually renders — not eyeballed. The count matters:
              there used to be one fewer <col> than <th>, so the last column
              (Errors) fell off the end of the colgroup and absorbed whatever
              slack `w-full` left over, while Started — 9rem for a 21-character
              `2026-09-15 05:26 CEST` that is `whitespace-nowrap` — overflowed
              into Finished and read as one run-on timestamp.

              Widest content per column, at the text size it renders in:
              (copy)   an icon button only — 2.25rem, no header text.
              Started  the key column: `2026-09-15 05:26 CEST` + trigger/ingress
                       badges + the disclosure gutter. It replaced a Session
                       column holding stamps like `ingress-34939121211-1`, which
                       overflowed onto Component on every CDID row.
              Runner   the `Self-hosted` badge, text-[10px].
              Outcome  the badge, and nothing else — the skip reason moved to
                       the session page, which is what paid for 4rem here.
              Finished `06:18 CEST`, or `06:18 CEST +1d` when the session
                       crossed local midnight — the day offset says what a
                       repeated full date used to, in a third of the width.
              Duration `52m 03s`; a running row's `(elapsed)` wraps.
              The four counts are header-bound — `Resolved`/`Deferred`/
              `Warnings` are wider than any number that lands under them. */}
          <colgroup>
            <col className="w-[2.25rem]" />
            <col className="w-[12rem]" />
            {kind === 'cdid' && <col className="w-[11rem]" />}
            <col className="w-[5.5rem]" />
            <col className="w-[6.5rem]" />
            {kind === 'cdid' && <col className="w-[9.5rem]" />}
            <col className="w-[7rem]" />
            <col className="w-[5.5rem]" />
            {kind === 'ahc' ? (
              <col className="w-[7rem]" />
            ) : (
              <>
                <col className="w-[5rem]" />
                <col className="w-[5rem]" />
              </>
            )}
            <col className="w-[5rem]" />
            <col className="w-[4.5rem]" />
          </colgroup>
          <thead className="bg-zinc-50 dark:bg-zinc-900 text-left text-xs text-zinc-500">
            <tr>
              <th className="px-1 py-2 font-medium">
                <span className="sr-only">Copy link to run</span>
              </th>
              <th className="px-3 py-2 font-medium">Started</th>
              {kind === 'cdid' && <th className="px-3 py-2 font-medium">Component</th>}
              <th className="px-3 py-2 font-medium">Runner</th>
              <th className="px-3 py-2 font-medium">Outcome</th>
              {kind === 'cdid' && <th className="px-3 py-2 font-medium">Stage</th>}
              <th className="px-3 py-2 font-medium">Finished</th>
              <th className="px-3 py-2 font-medium">Duration</th>
              {kind === 'ahc' ? (
                <th className="px-3 py-2 font-medium text-right">Tickets Filed</th>
              ) : (
                <>
                  <th className="px-3 py-2 font-medium text-right">Resolved</th>
                  <th className="px-3 py-2 font-medium text-right">Deferred</th>
                </>
              )}
              <th className="px-3 py-2 font-medium text-right">Warnings</th>
              <th className="px-3 py-2 font-medium text-right">Errors</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800 bg-white dark:bg-zinc-950">
            {groups.map(({ session, children }) => {
              const isOpen = expanded.has(session.stamp);
              const failed = children.filter((c) => c.outcome === 'failure').length;
              return (
                <Fragment key={session.stamp}>
                  <SessionRow
                    s={session}
                    now={now}
                    dispatched={children}
                    leading={
                      children.length > 0 ? (
                        <button
                          type="button"
                          aria-expanded={isOpen}
                          aria-label={`${isOpen ? 'Hide' : 'Show'} ${children.length} dispatched ${
                            children.length === 1 ? 'session' : 'sessions'
                          } for the run started ${fmtLocal(session.startedAt)}`}
                          onClick={() =>
                            setExpanded((prev) => {
                              const next = new Set(prev);
                              if (!next.delete(session.stamp)) next.add(session.stamp);
                              return next;
                            })
                          }
                          className="flex items-center gap-1 rounded px-1 text-[11px] tabular-nums text-zinc-500 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400"
                        >
                          <span aria-hidden className="inline-block w-2">
                            {isOpen ? '▾' : '▸'}
                          </span>
                          {children.length}
                          {/* Failures inside a collapsed subtree are the one
                              thing that must survive collapsing: a cycle whose
                              orchestrator succeeded while three workers died is
                              not a clean cycle. */}
                          {failed > 0 && (
                            <span className="text-red-600 dark:text-red-400">
                              ({failed} failed)
                            </span>
                          )}
                        </button>
                      ) : null
                    }
                  />
                  {isOpen &&
                    children.map((child) => (
                      <SessionRow
                        key={child.stamp}
                        s={child}
                        now={now}
                        leading={
                          <span
                            aria-hidden
                            className="inline-block w-4 border-l border-b border-zinc-300 dark:border-zinc-700 h-2 -mt-2 ml-1"
                          />
                        }
                      />
                    ))}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
